import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  FormControl,
  Validators,
} from '@angular/forms';
import { MatError } from '@angular/material/form-field';
import { RouterModule } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  CustomValidator,
  matchingPasswords,
} from 'src/app/_utlis/app-validators';

@Component({
    selector: 'app-password-form',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, RouterModule, MatError,
        TranslatePipe
    ],
    templateUrl: './password-form.component.html',
    styleUrl: './password-form.component.scss'
})
export class PasswordFormComponent {
  @Input() translation: any = {};
  @Input() urlFront: string = '';
  @Input() showTermsCheckbox: boolean = true;
  @Input() initialFormValues: any = {};
  @Input() formType: string = 'reset';
  @Input() showCurrentPassword: boolean = false;
  @Input() isLoading: boolean = false;

  @Output() formSubmit = new EventEmitter<any>();
  @Output() formChange = new EventEmitter<FormGroup>();

  @ViewChild('passwordInput') passwordInput!: ElementRef;

  passwordForm!: FormGroup;
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;
  passwordStrength: number = 0;
  passwordMatch: boolean | null = null;
  showCurrent: boolean = false;

  requirements = {
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  };

  constructor(private fb: FormBuilder, private translate: TranslateService) {}

  ngOnInit(): void {
    this.initForm();

    // Emit the form to parent component for access
    this.formChange.emit(this.passwordForm);

    // Subscribe to password changes
    this.passwordForm.get('password')?.valueChanges.subscribe((password) => {
      this.checkPasswordRequirements(password);
      this.checkPasswordStrength(password);
      this.checkPasswordMatch();
    });

    // Subscribe to confirm password changes
    this.passwordForm
      .get('password_confirmation')
      ?.valueChanges.subscribe(() => {
        this.checkPasswordMatch();
      });
  }

  initForm(): void {
    // Create form with or without terms checkbox
    const formControls: any = {
      password: new FormControl(
        this.initialFormValues.password || '',
        Validators.compose([
          Validators.required,
          Validators.minLength(8),
          CustomValidator.patternValidator(/\d/, { hasNumber: true }),
          CustomValidator.patternValidator(/[a-zA-Z]/, { hasAlphabet: true }),
        ])
      ),
      password_confirmation: new FormControl(
        this.initialFormValues.password_confirmation || '',
        Validators.compose([Validators.required])
      ),
    };
    // Add current password control if needed
    if (this.showCurrentPassword) {
      formControls.current_password = new FormControl(
        this.initialFormValues.current_password || '',
        Validators.compose([Validators.required])
      );
    }
    // Add terms control if needed
    if (this.showTermsCheckbox) {
      formControls.accept_terms = new FormControl(
        this.initialFormValues.accept_terms || true
      );
    }

    // Create form group with matching passwords validator
    this.passwordForm = this.fb.group(formControls, {
      validator: matchingPasswords('password', 'password_confirmation'),
    });
  }

  togglePasswordVisibility(field: string = 'password'): void {
    if (field === 'password') {
      this.showPassword = !this.showPassword;
    } else if (field === 'confirmPassword') {
      this.showConfirmPassword = !this.showConfirmPassword;
    } else if (field === 'currentPassword') {
      this.showCurrent = !this.showCurrent;
    }
  }
  // toggleConfirmPasswordVisibility(): void {
  //   this.showConfirmPassword = !this.showConfirmPassword;
  // }

  checkPasswordRequirements(password: string): void {
    this.requirements.length = password.length >= 8;
    this.requirements.uppercase = /[A-Z]/.test(password);
    this.requirements.lowercase = /[a-z]/.test(password);
    this.requirements.number = /[0-9]/.test(password);
    this.requirements.special = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  }

  checkPasswordStrength(password: string): void {
    if (!password) {
      this.passwordStrength = 0;
      return;
    }

    let strength = 0;
    if (this.requirements.length) strength++;
    if (this.requirements.uppercase) strength++;
    if (this.requirements.lowercase) strength++;
    if (this.requirements.number) strength++;
    if (this.requirements.special) strength++;

    this.passwordStrength = Math.min(5, strength);
  }

  checkPasswordMatch(): void {
    const password = this.passwordForm.get('password')?.value;
    const confirmPassword = this.passwordForm.get(
      'password_confirmation'
    )?.value;

    if (!confirmPassword) {
      this.passwordMatch = null;
      return;
    }

    this.passwordMatch = password === confirmPassword;
  }

  getPasswordStrengthData(): { text: string; translationKey: string } {
    const strengthData = {
      0: {
        text: this.translate.instant('reset_password.very_weak') || 'Very Weak',
        translationKey: 'reset_password_very_weak',
      },
      1: {
        text: this.translate.instant('reset_password.strength_weak') || 'Weak',
        translationKey: 'reset_password_strength_weak',
      },
      2: {
        text: this.translate.instant('reset_password.strength_fair') || 'Fair',
        translationKey: 'reset_password_strength_fair',
      },
      3: {
        text: this.translate.instant('reset_password.strength_good') || 'Good',
        translationKey: 'reset_password_strength_good',
      },
      4: {
        text: this.translate.instant('reset_password.strength_strong') || 'Strong',
        translationKey: 'reset_password_strength_strong',
      },
      5: {
        text:
          this.translate.instant('reset_password.strenght_very_strong') ||
          'Very Strong',
        translationKey: 'reset_password_strenght_very_strong',
      },
    };

    return (
      strengthData[this.passwordStrength as keyof typeof strengthData] ||
      strengthData[0]
    );
  }

  onSubmit(): void {
    if (this.passwordForm.valid) {
      // If terms checkbox is shown, validate it's checked
      if (
        this.showTermsCheckbox &&
        !this.passwordForm.get('accept_terms')?.value
      ) {
        return;
      }

      this.formSubmit.emit(this.passwordForm.value);
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.passwordForm.controls).forEach((key) => {
        this.passwordForm.get(key)?.markAsTouched();
      });
    }
  }

  isFormValid(): boolean {
    return (
      this.passwordForm.valid &&
      this.passwordMatch === true &&
      (!this.showTermsCheckbox ||
        this.passwordForm.get('accept_terms')?.value === true)
    );
  }
}
